/**
 * Onboarding Life Performance — §A (profil sportif paramétrable).
 *
 * Tant qu'aucun sport n'est paramétré, le module présente cette activation.
 * On choisit un ou plusieurs sports du catalogue partagé, puis pour chacun on
 * règle le niveau, l'orientation et la fréquence cible. Les contraintes fines
 * (jours dispo, matériel, blessures) restent éditables ensuite dans Réglages.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'

import { performanceApi } from '../api/performanceApi'
import { Loading, PERF_ACCENT, cardSx, darkFieldSx } from './shared'

const LEVELS = [
  { value: 'beginner', label: 'Débutant' },
  { value: 'intermediate', label: 'Intermédiaire' },
  { value: 'advanced', label: 'Avancé' },
]

const FOCUS = [
  { value: 'performance', label: 'Performance' },
  { value: 'health', label: 'Santé' },
  { value: 'aesthetics', label: 'Esthétique' },
  { value: 'weight_loss', label: 'Perte de poids' },
  { value: 'strength', label: 'Force' },
  { value: 'endurance', label: 'Endurance' },
  { value: 'mobility', label: 'Mobilité' },
]

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

export default function OnboardingView({ onDone }) {
  const [sports, setSports] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState({}) // { sportId: {level, focus, target, days[]} }
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      const list = await performanceApi.sports.list()
      setSports(list)
      setLoading(false)
    })()
  }, [])

  const toggleSport = (sport) => {
    setSelected((prev) => {
      const next = { ...prev }
      if (next[sport.id]) {
        delete next[sport.id]
      } else {
        next[sport.id] = {
          level: 'beginner',
          focus: 'health',
          target: 2,
          days: [],
        }
      }
      return next
    })
  }

  const update = (id, field, value) =>
    setSelected((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }))

  const toggleDay = (id, day) =>
    setSelected((prev) => {
      const days = prev[id].days
      const next = days.includes(day)
        ? days.filter((d) => d !== day)
        : [...days, day].sort((a, b) => a - b)
      return { ...prev, [id]: { ...prev[id], days: next } }
    })

  const submit = async () => {
    setSaving(true)
    try {
      for (const [sportId, cfg] of Object.entries(selected)) {
        await performanceApi.preferences.create({
          sport: Number(sportId),
          level: cfg.level,
          focus: cfg.focus,
          target_sessions_per_week: Number(cfg.target) || 1,
          available_weekdays: cfg.days,
          is_active: true,
        })
      }
      onDone()
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Loading label="Chargement du catalogue de sports…" />

  const count = Object.keys(selected).length

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', py: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <BoltRoundedIcon sx={{ color: PERF_ACCENT }} />
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 24 }}>
          Activez votre espace performance
        </Typography>
      </Stack>
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
        Choisissez les sports que vous pratiquez. Pour chacun, indiquez votre
        niveau, votre orientation et le nombre de séances visées par semaine.
        Tout reste modifiable ensuite dans Réglages.
      </Typography>

      <Box sx={{ ...cardSx, mb: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}>
          Vos sports ({count} sélectionné{count > 1 ? 's' : ''})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {sports.map((s) => (
            <Chip
              key={s.id}
              label={s.name}
              onClick={() => toggleSport(s)}
              sx={{
                bgcolor: selected[s.id] ? PERF_ACCENT : 'rgba(255,255,255,0.08)',
                color: selected[s.id] ? '#0b0f1f' : '#fff',
                fontWeight: 600,
              }}
            />
          ))}
        </Box>
      </Box>

      {sports
        .filter((s) => selected[s.id])
        .map((s) => {
          const cfg = selected[s.id]
          return (
            <Box key={s.id} sx={{ ...cardSx, mb: 2 }}>
              <Typography sx={{ color: PERF_ACCENT, fontWeight: 700, mb: 1.5 }}>
                {s.name}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Niveau"
                  value={cfg.level}
                  onChange={(e) => update(s.id, 'level', e.target.value)}
                  fullWidth
                  sx={darkFieldSx}
                >
                  {LEVELS.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Orientation"
                  value={cfg.focus}
                  onChange={(e) => update(s.id, 'focus', e.target.value)}
                  fullWidth
                  sx={darkFieldSx}
                >
                  {FOCUS.map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="number"
                  size="small"
                  label="Séances/sem."
                  value={cfg.target}
                  onChange={(e) => update(s.id, 'target', e.target.value)}
                  sx={{ ...darkFieldSx, width: 130 }}
                  inputProps={{ min: 1, max: 14 }}
                />
              </Stack>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 0.75 }}
              >
                Jours disponibles
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {WEEKDAYS.map((d, i) => (
                  <Chip
                    key={d}
                    label={d}
                    size="small"
                    onClick={() => toggleDay(s.id, i)}
                    sx={{
                      bgcolor: cfg.days.includes(i)
                        ? 'rgba(255,106,61,0.25)'
                        : 'rgba(255,255,255,0.06)',
                      color: '#fff',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )
        })}

      <Button
        fullWidth
        variant="contained"
        disabled={saving || count === 0}
        onClick={submit}
        sx={{
          bgcolor: PERF_ACCENT,
          color: '#0b0f1f',
          fontWeight: 700,
          py: 1.2,
          mt: 1,
          '&:hover': { bgcolor: '#ff7d56' },
        }}
      >
        {saving ? 'Activation…' : 'Activer Life Performance'}
      </Button>
    </Box>
  )
}
